import { default as React } from 'react';
import {
  Box,
  DialogContent,
  DialogContentText,
  Paper,
  Switch,
  Table as BackstageTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { WorkloadClient } from '@internal/workload-api-helper';
import _ from 'lodash';
import { useState } from 'react';
import { BaseDialog } from '../BaseDialog';
import { CustomTooltip } from '../CustomTooltip';
import PaginatedTable from '../about-card/PaginatedTable';
import { Vpc } from '../types';
import { PdsAlert, PdsLink } from '@principal/design-system-react';

const max_vpc_count = 5;

type RegionVpcCount = {
  [region: string]: number;
};

// Define type to use in this component to normalize the feature set data points
type ManagedVpcFeatureSet = {
  region: string;
  vpc_enabled: boolean;
  public_subnets_enabled: boolean;
};

interface VpcSelectionDialogProps {
  open: boolean;
  handleClose: () => void;
  callPatchApi: (vpcs: Vpc[]) => Promise<any>;
  workloadClient: WorkloadClient;
  accountId: string;
  vpcs: ManagedVpcFeatureSet[];
  vpcCount: RegionVpcCount | undefined;
  cardContentClass: string;
}

export const VpcSelectionDialog = ({
  open,
  handleClose,
  callPatchApi,
  workloadClient,
  accountId,
  vpcs,
  vpcCount,
}: VpcSelectionDialogProps) => {
  interface ManagedVpcMap {
    [region: string]: {
      vpc_enabled: boolean;
      public_subnets_enabled: boolean;
    };
  }

  const accInit: ManagedVpcMap = {};
  const [dialogState, setDialogState] = React.useState(
    vpcs.reduce(
      (acc, current) => ({
        ...acc,
        [current.region]: {
          vpc_enabled: current.vpc_enabled,
          public_subnets_enabled: current.public_subnets_enabled,
        },
      }),
      accInit,
    ),
  );

  const [vpcResourcesNeedToBeDeleted, setVpcResourcesNeedToBeDeleted] =
    useState<boolean>();
  const [vpcResourcesBlockingDelete, setVpcResourcesBlockingDelete] =
    useState<{ accountId: string; name: string; arn: string; id: string }[]>();
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [failedFetchAccountResources, setFailedFetchAccountResources] =
    useState<boolean>(false);

  // Function to reset dialog state to original VPC values
  const resetDialogState = () => {
    const resetState = vpcs.reduce(
      (acc, current) => ({
        ...acc,
        [current.region]: {
          vpc_enabled: current.vpc_enabled,
          public_subnets_enabled: current.public_subnets_enabled,
        },
      }),
      accInit,
    );
    setDialogState(resetState);
    setVpcResourcesNeedToBeDeleted(undefined);
    setVpcResourcesBlockingDelete(undefined);
    setFailedFetchAccountResources(false);
  };

  // Enhanced handleClose to reset state when cancelled
  const handleDialogClose = () => {
    resetDialogState();
    handleClose();
  };

  const getAccountVpcResources = async (
    region: string,
    public_subnet: boolean,
  ) => {
    const res = await workloadClient.apiFetch(
      `workloads/${accountId}/features/networking/vpc-resources?region=${region}&publicSubnet=${public_subnet}`,
      'GET',
    );
    if (res.status !== 200) {
      return false;
    }
    const resp = await res.json();
    return resp.resources['non-foundation-managed'];
  };

  const getAccountR53Resources = async () => {
    const res = await workloadClient.apiFetch(
      `workloads/${accountId}/features/networking/dns-records`,
      'GET',
    );
    if (res.status !== 200) {
      return false;
    }
    const resp = await res.json();
    return resp.resources;
  };

  const getAccountVpcResourcesToDelete = async (
    region: string,
    public_subnet: boolean,
  ) => {
    const non_foundation_managed_resources = await getAccountVpcResources(
      region,
      public_subnet,
    );

    // Returns false on error, pass that to caller
    if (non_foundation_managed_resources === false) {
      return false;
    }

    const map: { accountId: string; name: string; arn: string; id: string }[] =
      non_foundation_managed_resources.map(
        (obj: {
          accountId: string;
          name: string;
          properties: { providerUniqueId: string };
          id: string;
        }) => ({
          accountId: accountId,
          name: obj.name,
          arn: obj.properties.providerUniqueId!,
          id: obj.id,
        }),
      );

    return map;
  };

  function updateVpcEnabledState(event: React.ChangeEvent<HTMLInputElement>) {
    const region = event.target.name;
    const vpcEnabled = !dialogState[region].vpc_enabled;
    const publicSubnetsEnabled = vpcEnabled
      ? dialogState[region].public_subnets_enabled
      : false;
    setDialogState({
      ...dialogState,
      [region]: {
        vpc_enabled: vpcEnabled,
        public_subnets_enabled: publicSubnetsEnabled,
      },
    });
  }

  function updatePublicSubnetsEnabledState(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const region = event.target.name;
    const publicSubnetsEnabled = !dialogState[region].public_subnets_enabled;
    setDialogState({
      ...dialogState,
      [region]: {
        vpc_enabled: dialogState[region].vpc_enabled,
        public_subnets_enabled: publicSubnetsEnabled,
      },
    });
  }

  function isMaxVpcCountForRegion(
    region: string,
    vpc_enabled: boolean,
  ): boolean {
    if (vpcCount) {
      return (
        (vpcCount[region] >= max_vpc_count && !vpc_enabled) ||
        vpcCount[region]?.toString()?.startsWith('error')
      );
    }
    return false;
  }

  function getEntryWithRegion(data: any[], region: string) {
    return data.find(entry => entry.region === region);
  }

  const ResourcesBlockingDelete = (resourceProps: {
    resources: { accountId: string; name: string; arn: string; id: string }[];
    title: string;
  }) => {
    if (resourceProps?.resources?.length > 0) {
      return (
        <>
          <Box pb={1} pt={1} pl={1} pr={1} fontWeight="bold">
            {resourceProps.title}
          </Box>
          <PaginatedTable rows={resourceProps.resources} />
          <Box pb={1} pt={1} pl={1} pr={1}>
            All resources listed above must be removed before this change can be
            submitted. If you've just removed a resource, allow a few minutes
            for it to be reflected here before retrying.
          </Box>
        </>
      );
    }

    return <></>;
  };

  const title = `Update Foundation Managed VPC`;
  const contents = (
    <DialogContent style={{ padding: '0px 0px 0px 0px' }}>
      {failedFetchAccountResources && (
        <div
          style={{
            color: '#222222',
          }}
        >
          <PdsAlert variant="error">
            An error occurred while verifying the requested action can be
            performed. Please try again. If you continue to see an error and
            believe you have no resources remaining in any VPCs, Subnets, or
            Hosted Zones (if disabling all VPCs) you requested be disabled,
            please submit a request for{' '}
            <PdsLink
              variant="strong"
              href="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/support/#internal-support"
            >
              support
            </PdsLink>{' '}
            to the network team.
          </PdsAlert>
        </div>
      )}
      {!vpcResourcesNeedToBeDeleted && !failedFetchAccountResources && (
        <TableContainer component={Paper}>
          <BackstageTable>
            <TableHead>
              <TableRow>
                <TableCell>Operating region</TableCell>
                <TableCell align="left">Managed VPC</TableCell>
                <TableCell align="left">Public subnets</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vpcs &&
                vpcs.map(vpc => (
                  <React.Fragment key={`${vpc.region}_allowed`}>
                    <TableRow>
                      <TableCell align="left" component="th" scope="row">
                        {vpc.region}
                      </TableCell>
                      <TableCell align="left">
                        <Switch
                          id={`${vpc.region}_managedvpc_enabled`}
                          name={vpc.region}
                          checked={dialogState[vpc.region].vpc_enabled}
                          onChange={updateVpcEnabledState}
                          disabled={isMaxVpcCountForRegion(
                            vpc.region,
                            vpc.vpc_enabled,
                          )}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Switch
                          id={`${vpc.region}_public_subnets_enabled`}
                          name={vpc.region}
                          checked={
                            dialogState[vpc.region].public_subnets_enabled
                          }
                          onChange={updatePublicSubnetsEnabledState}
                          disabled={!dialogState[vpc.region].vpc_enabled}
                        />
                      </TableCell>
                      <TableCell align="left">
                        &nbsp;
                        {isMaxVpcCountForRegion(
                          vpc.region,
                          vpc.vpc_enabled,
                        ) && (
                          <CustomTooltip
                            detail={
                              <Typography variant="body1">
                                Region already has the maximum VPCs allowed (
                                {max_vpc_count}). Cannot add another VPC until
                                one is removed.
                              </Typography>
                            }
                            htmlColor="yellow"
                            icon="warning"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          </BackstageTable>
        </TableContainer>
      )}
      {vpcResourcesNeedToBeDeleted && !failedFetchAccountResources && (
        <DialogContentText>
          <ResourcesBlockingDelete
            resources={vpcResourcesBlockingDelete!}
            title="VPC Resources Preventing Changes"
          />
        </DialogContentText>
      )}
    </DialogContent>
  );

  const updated_vpcs = React.useMemo(() => {
    const result: any[] = [];
    for (const key in dialogState) {
      if (Object.hasOwnProperty.call(dialogState, key)) {
        result.push({
          region: key,
          ...dialogState[key],
        });
      }
    }
    return result;
  }, [dialogState]);

  async function handleConfirm(e: any) {
    e.preventDefault();
    setSubmitInProgress(true);
    const current_vpcs = vpcs;
    const resource_checks: any[] = [];
    let all_disabled = true;

    current_vpcs.forEach(vpc => {
      const updated_entry = getEntryWithRegion(updated_vpcs, vpc.region);
      if (
        vpc.vpc_enabled === true &&
        vpc.vpc_enabled !== updated_entry.vpc_enabled
      ) {
        // They have disabled VPC in this region, check all resources
        resource_checks.push({
          region: vpc.region,
          publicSubnet: false,
        });
      } else if (
        vpc.public_subnets_enabled === true &&
        vpc.public_subnets_enabled !== updated_entry.public_subnets_enabled
      ) {
        // The have disabled only public subnet in this region, check only that
        resource_checks.push({
          region: vpc.region,
          publicSubnet: true,
        });
      } else if (updated_entry.vpc_enabled !== false) {
        // Found at least one VPC enabled; no need to check route 53 resources
        all_disabled = false;
      }
    });

    // Check if resources are present in disabled vpc or public subnet
    const resources: any[] = [];
    const resourcePromises = resource_checks.map(resource_check =>
      getAccountVpcResourcesToDelete(
        resource_check.region,
        resource_check.publicSubnet,
      ),
    );
    resources.push(...(await Promise.all(resourcePromises)));

    // If all VPCs are being disabled, also check for R53 resources
    if (all_disabled) {
      resources.push(getAccountR53Resources());
    }

    const flat_resources = (await Promise.all(resources)).flat();

    setFailedFetchAccountResources(false);
    if (flat_resources.includes(false)) {
      setFailedFetchAccountResources(true);
      setSubmitInProgress(false);
      return;
    }

    setVpcResourcesBlockingDelete(flat_resources);
    if (flat_resources.length > 0) {
      setVpcResourcesNeedToBeDeleted(true);
      setSubmitInProgress(false);
    } else {
      setVpcResourcesNeedToBeDeleted(false);
    }

    if (!_.isEqual(current_vpcs, updated_vpcs) && flat_resources.length === 0) {
      // Update Catalog File
      await callPatchApi(updated_vpcs);
      setSubmitInProgress(false);
      handleClose(); // Use original handleClose on successful confirm
    }
  }

  return (
    <BaseDialog
      open={open}
      handleClose={handleDialogClose} // Use enhanced close handler
      handleConfirm={handleConfirm}
      disableConfirm={
        vpcResourcesNeedToBeDeleted! ||
        submitInProgress ||
        failedFetchAccountResources! ||
        _.isEqual(vpcs, updated_vpcs)
      }
      isLoading={submitInProgress}
      title={title}
    >
      {contents}
    </BaseDialog>
  );
};
